/**
 * Serializes the day / month / year birthday inputs into a plain ISO date string ("YYYY-MM-DD").
 *
 * The string is built directly from the numeric parts and never round-trips through
 * `new Date(...).toISOString()`, so a local->UTC conversion can never roll the calendar day back
 * for users in a positive-offset timezone. Returns `undefined` when a part is empty/nullish, when
 * the year is not a plausible 4-digit year, or when the parts do not form a real calendar date.
 */
export const formatBirthday = (
  dd: string | number | null | undefined,
  mm: string | number | null | undefined,
  yy: string | number | null | undefined
): string | undefined => {
  if (dd === '' || mm === '' || yy === '' || dd == null || mm == null || yy == null) {
    return undefined;
  }

  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yy);

  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
    return undefined;
  }

  // A birthday must be a real 4-digit year. Without this guard a 2-, 3- or 5-digit year would slip
  // through the calendar check below and be emitted as a malformed string (e.g. "198-04-29").
  if (year < 1000 || year > 9999) {
    return undefined;
  }

  // Reject impossible calendar dates (e.g. 31/02). The Date is constructed only to validate the
  // day/month/year combination; it never feeds the returned string, so no local->UTC conversion
  // can touch the output.
  const probe = new Date(year, month - 1, day);
  if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) {
    return undefined;
  }

  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${year}-${pad(month)}-${pad(day)}`;
};
